import React, { useEffect, useState } from "react";
import { useVault } from "@/context/VaultContext";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PasswordField } from "./PasswordField";
import { StrengthMeter } from "./StrengthMeter";
import { PasswordGenerator } from "./PasswordGenerator";
import { TotpCode } from "./TotpCode";
import { parseTotpInput, normalizeUrl } from "@/lib/vaultCrypto";
import { checkPassword, formatBreachCount } from "@/lib/breachCheck";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Star, Trash2, Share2, X, ExternalLink, Eye, Radar, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export const CATEGORIES = ["Bank og finans", "Offentlig", "E-post", "Sosiale medier", "Jobb", "Handel", "Underholdning", "Bolig og strøm", "Helse", "Annet"];

const EMPTY = { title: "", url: "", username: "", password: "", notes: "", category: "Annet", favorite: false, totp: "", password_changed_at: null };

/** Opprett/rediger oppføring. entry=null => ny. */
export const EntrySheet = ({ open, onOpenChange, entry, onSaved, onDeleted }) => {
  const { saveEntry, deleteEntry, shareEntry, revokeShare, entries, settings, breach } = useVault();
  const [form, setForm] = useState(EMPTY);
  // Live lekkasjesjekk av passordet i skjemaet (debounced, k-anonymitet – kun 5 hex-tegn sendes)
  const [pwCheck, setPwCheck] = useState({ state: "idle", count: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [targets, setTargets] = useState(null);
  const [shareBusy, setShareBusy] = useState("");
  const editing = !!entry?.id;
  const live = editing ? entries.find((e) => e.id === entry.id) || entry : null;
  const totpParsed = parseTotpInput(form.totp);

  useEffect(() => {
    if (open) {
      setForm(entry ? { ...EMPTY, ...entry } : EMPTY);
      setError("");
      if (entry?.id) api.get("/vault/share-targets").then((r) => setTargets(r.data.items)).catch(() => setTargets([]));
      else setTargets(null);
    }
  }, [open, entry]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  useEffect(() => {
    if (!open || settings?.breach_check_enabled === false) { setPwCheck({ state: "idle", count: 0 }); return; }
    const pw = form.password;
    if (!pw) { setPwCheck({ state: "idle", count: 0 }); return; }
    // Uendret passord på eksisterende oppføring: bruk resultatet fra hvelvsjekken
    if (editing && entry && pw === entry.password && breach?.status === "done") {
      setPwCheck({ state: "done", count: breach.results?.[entry.id] || 0 });
      return;
    }
    const ctrl = new AbortController();
    setPwCheck({ state: "checking", count: 0 });
    const t = setTimeout(() => {
      checkPassword(pw, { signal: ctrl.signal })
        .then((r) => { if (!ctrl.signal.aborted) setPwCheck({ state: "done", count: r.count }); })
        .catch((e) => { if (!ctrl.signal.aborted && e?.name !== "AbortError") setPwCheck({ state: "error", count: 0 }); });
    }, 700);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [form.password, open, editing, entry, settings?.breach_check_enabled, breach?.status, breach?.results]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("Gi oppføringen et navn."); return; }
    if (form.totp && !totpParsed) { setError("2FA-nøkkelen er ugyldig. Lim inn base32-nøkkelen eller otpauth://-lenken."); return; }
    setSaving(true);
    setError("");
    try {
      const data = { ...form, title: form.title.trim(), url: form.url.trim(), totp: totpParsed ? (form.totp.toLowerCase().startsWith("otpauth://") ? form.totp.trim() : totpParsed.secret) : "" };
      if (!editing || (entry && entry.password !== form.password)) data.password_changed_at = new Date().toISOString().slice(0, 10);
      // Mottakere som allerede har tilgang må få nytt innhold re-kryptert
      const recipients = (live?.shares || []).map((s) => (targets || []).find((t) => t.id === s.recipient_id)).filter((t) => t && t.public_key);
      const saved = await saveEntry(data, editing ? entry.id : null, recipients);
      toast.success(editing ? "Oppføring oppdatert" : "Passord lagret i hvelvet");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await deleteEntry(entry.id);
      toast.success("Oppføring slettet");
      setDelOpen(false);
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const doShare = async (t) => {
    setShareBusy(t.id);
    try {
      // Del gjeldende lagrede innhold (ikke ulagrede endringer)
      await shareEntry(live, t);
      toast.success(`Delt med ${t.full_name} (kun lesing)`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setShareBusy("");
    }
  };
  const doRevoke = async (recipientId) => {
    setShareBusy(recipientId);
    try {
      await revokeShare(entry.id, recipientId);
      toast.success("Deling trukket tilbake");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setShareBusy("");
    }
  };

  const sharedIds = new Set((live?.shares || []).map((s) => s.recipient_id));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-card overflow-y-auto" data-testid="entry-sheet">
        <SheetHeader>
          <SheetTitle>{editing ? "Rediger passord" : "Nytt passord"}</SheetTitle>
          <SheetDescription>{editing ? `Sist endret ${formatDate(live?.updated_at)}${live?.password_changed_at ? ` · passord byttet ${formatDate(live.password_changed_at)}` : ""}` : "Krypteres i nettleseren før det lagres."}</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-6 space-y-5" noValidate autoComplete="off">
          <div className="flex items-end gap-2">
            <div className="space-y-1.5 flex-1"><Label htmlFor="en-title">Navn *</Label><Input id="en-title" value={form.title} onChange={set("title")} placeholder="f.eks. Altinn, Nettbank, Netflix" data-testid="entry-title-input" /></div>
            <Button type="button" variant={form.favorite ? "secondary" : "outline"} size="icon" onClick={() => setForm((f) => ({ ...f, favorite: !f.favorite }))} aria-pressed={form.favorite} aria-label="Favoritt" data-testid="entry-favorite-toggle"><Star className={cn("h-4 w-4", form.favorite && "fill-current text-warn")} /></Button>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="en-url">Nettadresse</Label>
            <div className="flex gap-1">
              <Input id="en-url" value={form.url} onChange={set("url")} placeholder="https://…" inputMode="url" data-testid="entry-url-input" />
              {form.url ? <Button type="button" variant="ghost" size="icon" className="shrink-0" asChild><a href={normalizeUrl(form.url)} target="_blank" rel="noopener noreferrer" aria-label="Åpne" data-testid="entry-url-open"><ExternalLink className="h-4 w-4" /></a></Button> : null}
            </div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="en-user">Brukernavn / e-post</Label><Input id="en-user" value={form.username} onChange={set("username")} autoComplete="off" data-testid="entry-username-input" /></div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between"><Label htmlFor="en-pw">Passord</Label><PasswordGenerator onUse={(p) => setForm((f) => ({ ...f, password: p }))} /></div>
            <PasswordField id="en-pw" value={form.password} onChange={set("password")} testId="entry-password" />
            <StrengthMeter password={form.password} testId="entry-strength" />
            {form.password && pwCheck.state !== "idle" ? (
              <p className={cn("text-xs flex items-center gap-1.5", pwCheck.state === "done" && pwCheck.count ? "text-flag" : pwCheck.state === "done" ? "text-success" : "text-muted-foreground")} data-testid="entry-breach-check" data-state={pwCheck.state} aria-live="polite">
                {pwCheck.state === "checking" ? <><Loader2 className="h-3 w-3 animate-spin" /> Sjekker anonymt mot kjente lekkasjer …</> : null}
                {pwCheck.state === "done" && pwCheck.count ? <><Radar className="h-3 w-3" /> Funnet {formatBreachCount(pwCheck.count)} ganger i datalekkasjer – velg et annet passord.</> : null}
                {pwCheck.state === "done" && !pwCheck.count ? <><ShieldCheck className="h-3 w-3" /> Ikke funnet i kjente datalekkasjer.</> : null}
                {pwCheck.state === "error" ? <>Lekkasjesjekk utilgjengelig akkurat nå.</> : null}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="en-totp">2FA-nøkkel (TOTP) <span className="text-muted-foreground">– valgfritt</span></Label>
            <Input id="en-totp" value={form.totp} onChange={set("totp")} placeholder="Base32-nøkkel eller otpauth://…" className="font-mono" autoComplete="off" spellCheck={false} data-testid="entry-totp-input" />
            {form.totp ? (totpParsed ? <div className="flex items-center gap-2 text-xs text-muted-foreground"><span>Kode nå:</span><TotpCode secret={form.totp} compact testId="entry-totp" /></div> : <p className="text-xs text-destructive">Ugyldig nøkkel</p>) : <p className="text-xs text-muted-foreground">Lim inn nøkkelen fra tjenestens 2FA-oppsett («kan ikke skanne?»). Engangskoder genereres her i appen.</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.category || "Annet"} onValueChange={set("category")}>
                <SelectTrigger data-testid="entry-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="en-notes">Notater</Label><Textarea id="en-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="Sikkerhetsspørsmål, PIN-hint, kundenummer … (krypteres)" data-testid="entry-notes-input" /></div>

          {editing ? (
            <div className="rounded-lg border p-3 space-y-3" data-testid="entry-share-section">
              <div className="flex items-center gap-2"><Share2 className="h-4 w-4 text-primary" /><p className="text-sm font-medium">Deling (kun lesing)</p></div>
              {targets === null ? <p className="text-xs text-muted-foreground">Laster …</p> : targets.length === 0 ? (
                <p className="text-xs text-muted-foreground">Du kan dele med personer du har aktiv fullmakt-relasjon til under <strong>Familie og fullmakt</strong>. Ingen slike finnes ennå.</p>
              ) : (
                <ul className="space-y-2">
                  {targets.map((t) => {
                    const shared = sharedIds.has(t.id);
                    return (
                      <li key={t.id} className="flex items-center gap-2 text-sm">
                        <div className="min-w-0 flex-1"><p className="font-medium truncate">{t.full_name}</p><p className="text-xs text-muted-foreground truncate">{t.relation}{!t.has_vault ? " · har ikke satt opp hvelv ennå" : ""}</p></div>
                        {shared ? <Badge variant="outline" className="status-betalt border"><Eye className="h-3 w-3 mr-1" /> Kun lesing</Badge> : null}
                        {shared ? (
                          <Button type="button" variant="ghost" size="sm" className="h-8" disabled={shareBusy === t.id} onClick={() => doRevoke(t.id)} data-testid={`entry-share-revoke-${t.id}`}>{shareBusy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1" /> Trekk</>}</Button>
                        ) : (
                          <Button type="button" variant="outline" size="sm" className="h-8" disabled={!t.has_vault || shareBusy === t.id} onClick={() => doShare(t)} data-testid={`entry-share-add-${t.id}`}>{shareBusy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Del"}</Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-[11px] text-muted-foreground">Mottakeren ser oppføringen slik den er lagret nå. Endringer deles automatisk når du lagrer. Delingen fjernes hvis fullmakten avsluttes.</p>
            </div>
          ) : null}

          {error ? <p className="text-sm text-destructive" role="alert" data-testid="entry-form-error">{error}</p> : null}
          <div className="flex items-center gap-2 pt-2">
            {editing ? <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDelOpen(true)} data-testid="entry-delete-button"><Trash2 className="h-4 w-4 mr-2" /> Slett</Button> : null}
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="entry-cancel-button">Avbryt</Button>
              <Button type="submit" disabled={saving} data-testid="entry-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? "Lagre" : "Lagre i hvelvet"}</Button>
            </div>
          </div>
        </form>

        <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
          <AlertDialogContent className="bg-card">
            <AlertDialogHeader><AlertDialogTitle>Slette «{form.title}»?</AlertDialogTitle><AlertDialogDescription>Oppføringen og eventuelle delinger slettes permanent. Dette kan ikke angres.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="entry-delete-cancel">Avbryt</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="entry-delete-confirm">Slett</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};
